import constants from '@common/constants';
import { useUserStore } from '@common/store/useUserStore';
import { useUser } from '@common/hooks/useUser';
import { SimulatedDebt, TypeSelected } from '@common/apis/simulated-debt/types';
import useApiSimulatedDebt from '@common/apis/simulated-debt/useApiSimulatedDebt';
import { useDebtsContext } from '@common/contexts/DebtsContext/useDebtsContext';

const useSimulatedDebt = () => {
  const { document } = useUser();
  const simulatedDebts = useUserStore((state) => state.simulatedDebts);
  const { addSimulatedDebts, resetSimulatedDebts } = useUserStore();

  const { apiSimulatedDebt } = useApiSimulatedDebt();
  const storedReferralId = localStorage.getItem('REFERRALID');
  const referralId = storedReferralId ? JSON.parse(storedReferralId) : undefined;
  const { processedPartnersRef } = useDebtsContext();

  const partnersWithSimulatePreload = ['riachuelo', 'kroton', 'bancodobrasil', 'itau', 'via'];
  const shouldPreloadData = (partnerId: string) => partnersWithSimulatePreload.includes(partnerId);

  const cleanSimulatedDebtsCache = () => {
    processedPartnersRef.current = new Set();
    resetSimulatedDebts();
  };

  const getSimulatedDebtsInfo = async (debt: SimulatedDebt) => {
    const partnerId = debt?.partner?.identifier;

    const { dividas: newSimulatedDebts } = await apiSimulatedDebt.send({
      msUrl: debt.partner.msUrl,
      documento: document,
      partnerSystemIdentifier: partnerId,
      referralId,
    });
    const safeDebts = newSimulatedDebts || [];

    addSimulatedDebts({ ...simulatedDebts, [partnerId]: safeDebts });

    return safeDebts;
  };

  const findDebtByCondition = (debts: SimulatedDebt[], condition: (divida: SimulatedDebt) => boolean) => debts?.find(condition);

  const getCurrentSimulatedDebt = async (partialDebt: SimulatedDebt) => {
    let debts = simulatedDebts[partialDebt?.partner?.identifier];

    const isExpressOffer = debts?.length === 1 && debts?.[0]?.isExpressOffer;

    if (!debts || isExpressOffer) {
      debts = await getSimulatedDebtsInfo(partialDebt);
    }

    const filteredById = findDebtByCondition(debts, ({ id }) => id === partialDebt.id);

    const filteredByValue = findDebtByCondition(debts, ({ valor }) => valor === partialDebt?.opcaoPagamento?.[0]?.currentDebtValue);

    const filteredByContract = findDebtByCondition(
      debts,
      (divida) =>
        divida.contrato === partialDebt?.contrato && divida.credorId === partialDebt?.credorId && divida.sistemaId === partialDebt?.sistemaId
    );

    const simulatedDebt = debts?.[0];
    const currentDebt = filteredById || filteredByValue || filteredByContract || simulatedDebt;

    if (!currentDebt) return;

    currentDebt.partner = partialDebt.partner;
    currentDebt.negativeStatus = partialDebt.negativeStatus;

    const typeSelectedList = ['overdued_and_upcoming', 'overdued_only', 'with_abatement', 'with_reenrolment', 'without_reenrolment'];

    if (
      partialDebt.partner.name === 'Riachuelo' && // ????
      typeSelectedList.includes(currentDebt.identificador)
    ) {
      currentDebt.typeSelected = currentDebt.identificador as TypeSelected;
    }

    return currentDebt;
  };

  const checkExpiredInstallments = (debt: SimulatedDebt) => {
    const hasDebtTypeRiachuelo = (valorString: string) => {
      const result = debt?.opcaoPagamento.some(({ propriedades }) => {
        const optType = propriedades?.find(({ chave }) => chave === constants.riachuelo.keyRiachueloNegotiationType);

        return optType && optType?.valorString === valorString;
      });

      return result;
    };

    const { valueStringOverduedAndUpcoming: overdueUpcoming, valueStringOverduedOnly: orverdueOnly } = constants.riachuelo;

    const hasOverduedOnly = hasDebtTypeRiachuelo(orverdueOnly);
    const hasOverduedAndUpcoming = hasDebtTypeRiachuelo(overdueUpcoming);

    const extractOptionsByString = (valorString: string) => {
      return debt?.opcaoPagamento.filter(({ propriedades }) => {
        const optType = propriedades?.find(({ chave }) => chave === constants.riachuelo.keyRiachueloNegotiationType);
        return optType && optType?.valorString === valorString;
      });
    };

    const optionsOrverdueOnly = extractOptionsByString(orverdueOnly);
    const optionsOverdueUpcoming = extractOptionsByString(overdueUpcoming);

    return {
      optionsOrverdueOnly,
      optionsOverdueUpcoming,
      hasOverduedOnly,
      hasOverduedAndUpcoming,
    };
  };

  const checkReenrolment = (debt: SimulatedDebt) => {
    const hasKrotonReenrolment = (valorBoolean: boolean) => {
      return debt?.opcaoPagamento?.some(({ propriedades }) => {
        const optType = propriedades?.find(({ chave }) => chave === 'IS_REMATRICULA_INCLUDED');

        return optType && optType?.valorBoolean === valorBoolean;
      });
    };

    const hasWithoutReenrolment = hasKrotonReenrolment(false);
    const hasWithReenrolment = hasKrotonReenrolment(true);

    const extractOptionsByBoolean = (valorBoolean: boolean) => {
      return debt?.opcaoPagamento?.filter(({ propriedades }) => {
        const optType = propriedades?.find(({ chave }) => chave === 'IS_REMATRICULA_INCLUDED');
        return optType && optType?.valorBoolean === valorBoolean;
      });
    };

    const optionsWithReenrolment = extractOptionsByBoolean(true);
    const optionsWithoutReenrolment = extractOptionsByBoolean(false);

    return {
      optionsWithReenrolment,
      optionsWithoutReenrolment,
      hasWithoutReenrolment,
      hasWithReenrolment,
    };
  };

  const checkAbatement = (debt: SimulatedDebt) => {
    const isBancoBrasil = debt?.partner?.partnerIdentifier === 'bancodobrasil';
    const hasBBAbatement = debt?.opcaoPagamento?.find((option) => option?.percentualDesconto > 0);

    return isBancoBrasil && !!hasBBAbatement;
  };

  return {
    checkAbatement,
    checkExpiredInstallments,
    checkReenrolment,
    getCurrentSimulatedDebt,
    shouldPreloadData,
    referralId,
    cleanSimulatedDebtsCache,
  };
};

export default useSimulatedDebt;
